import * as THREE from 'three';
import { gsap } from 'gsap';
import GUI from 'lil-gui';
import { TRIS, FACE_OVAL, MOUTH_INNER, MOUTH_OUTER } from './data/faceMeshData';

function dist(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }
function avgPts(idx, src) {
  const s = idx.map(i => src[i]);
  return { x: s.reduce((a, p) => a + p.x, 0) / s.length, y: s.reduce((a, p) => a + p.y, 0) / s.length };
}
function smooth(t) { t = Math.max(0, Math.min(1, t)); return t * t * (3 - 2 * t); }

export default class MemberMeshWarp {
  constructor() {
    this.canvas = null;
    this.renderer = null;
    this._createMainRenderer();

    // Debug overlay canvas (created once, reused)
    this._overlayCanvas = document.createElement('canvas');
    this._overlayCanvas.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;border-radius:0.4rem;z-index:10;';
    this._overlayCtx = this._overlayCanvas.getContext('2d');

    this.scene = null;
    this.camera = null;
    this.facePosAttr = null;
    this.faceColAttr = null;
    this.skinBgMat = null;
    this.faceCenter = null;
    this.faceSpherizeR = 0;

    this.currentMemberId = null;
    this.baseLM = null;
    this.curLM = null;
    this.W = 0;
    this.H = 0;
    this.isAnimating = false;
    this.coveredCanvas = null;
    this.landmarksData = null;

    this._rafId = null;

    // Active return animations (previous members fading out)
    this._returnSlots = [];

    // Mouse-driven parameter modulation
    this._mouse = {
      targetX: 0, targetY: 0,
      curX: 0, curY: 0,
      velX: 0, velY: 0,
      stiff: 0.06, damp: 0.82,
      influence: 0.9,   // how strongly mouse position affects params (0-2)
      active: false,
    };

    // Zoom
    this.zoomAmount = 1.6;
    this._memberZoom = {
      'base': 1.75,
      'base-3': 1.8,
      'base-4': 1.8,
      'base-6': 1.7,
      'base-7': 1.8,
      'base-8': 1.95,
    };
    this.zoom = { value: 1, cx: 0, cy: 0 };
    this.zoomTween = null;

    // Per-param spring configs: [stiffness, damping]
    // stiffness = pull strength, damping = bounce (lower = more bouncy)
    this._springCfg = {
      // stiff = pull force (lower=slower arrival), damp = friction (higher=more bounce/oscillation)
      bulge:      { stiff: 0.08, damp: 0.88, delay: 0 },
      mouth:      { stiff: 0.06, damp: 0.89, delay: 3 },
      eye:        { stiff: 0.10, damp: 0.87, delay: 1 },
      nose:       { stiff: 0.05, damp: 0.90, delay: 4 },
      spherize:   { stiff: 0.04, damp: 0.91, delay: 6 },
      eyeRotL:    { stiff: 0.09, damp: 0.88, delay: 2 },
      eyeRotR:    { stiff: 0.08, damp: 0.88, delay: 3 },
      noseRot:    { stiff: 0.06, damp: 0.90, delay: 7 },
      eyeShiftX:  { stiff: 0.07, damp: 0.89, delay: 1 },
      eyeShiftY:  { stiff: 0.06, damp: 0.89, delay: 5 },
      noseShiftX: { stiff: 0.05, damp: 0.90, delay: 6 },
      noseShiftY: { stiff: 0.045, damp: 0.90, delay: 8 },
      mouthShiftX: { stiff: 0.06, damp: 0.89, delay: 3 },
      mouthShiftY: { stiff: 0.05, damp: 0.89, delay: 4 },
    };
    // Return spring (shared)
    this._returnSpring = { stiff: 0.05, damp: 0.90 };

    // Max range per param (GUI adjustable)
    this._paramRange = {
      bulge:      0,
      mouth:      0.8,
      eye:        0.5,
      nose:       0.4,
      spherize:   0.25,
      eyeRotL:    0.7,
      eyeRotR:    0.7,
      noseRot:    0.4,
      eyeShiftX:  0.4,
      eyeShiftY:  0.4,
      noseShiftX: 0.4,
      noseShiftY: 0.4,
      mouthShiftX: 0.4,
      mouthShiftY: 0.4,
    };

    // Preview mode: directly set param values via GUI (bypasses random)
    // Initial values = paramRange values so you can adjust from the max baseline
    this._preview = {
      bulge:      this._paramRange.bulge,
      mouth:      this._paramRange.mouth,
      eye:        this._paramRange.eye,
      nose:       this._paramRange.nose,
      spherize:   this._paramRange.spherize,
      eyeRotL:    this._paramRange.eyeRotL,
      eyeRotR:    this._paramRange.eyeRotR,
      noseRot:    this._paramRange.noseRot,
      eyeShiftX:  this._paramRange.eyeShiftX,
      eyeShiftY:  this._paramRange.eyeShiftY,
      noseShiftX: this._paramRange.noseShiftX,
      noseShiftY: this._paramRange.noseShiftY,
      mouthShiftX: this._paramRange.mouthShiftX,
      mouthShiftY: this._paramRange.mouthShiftY,
      enabled: false,
    };

    this._initGUI();

    // Face feature anchors (computed per member)
    this._faceAnchors = null;
  }

  _createMainRenderer() {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'member-item__warp-canvas';
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x000000, 0);
  }

  /**
   * Spawn an independent return animation for the previous member.
   * Hands off the current canvas+renderer+scene to a self-contained loop.
   */
  _spawnReturnAnim() {
    if (!this.isAnimating || !this.scene) return;

    // Stop current render loop
    this._stopRenderLoop();
    if (this.zoomTween) { this.zoomTween.kill(); this.zoomTween = null; }

    // Capture all state needed for the return animation
    const slot = {
      canvas: this.canvas,
      renderer: this.renderer,
      scene: this.scene,
      camera: this.camera,
      facePosAttr: this.facePosAttr,
      backPosAttr: this._backPosAttr,
      faceColAttr: this.faceColAttr,
      skinBgMat: this.skinBgMat,
      baseLM: this.baseLM,
      curLM: this.curLM,
      faceCenter: this.faceCenter,
      faceSpherizeR: this.faceSpherizeR,
      faceAnchors: this._faceAnchors,
      W: this.W,
      H: this.H,
      paramCur: { ...this._paramCur },
      paramVel: { ...this._paramVel },
      paramTarget: {},
      zoom: { ...this.zoom },
      zoomTween: null,
      rafId: null,
    };

    // All targets → 0 for return
    for (const k of Object.keys(this._springCfg)) {
      slot.paramTarget[k] = 0;
    }

    // Start zoom-out tween
    slot.zoomTween = gsap.to(slot.zoom, {
      duration: 0.6,
      ease: 'power4.out',
      value: 1,
      cx: slot.W / 2,
      cy: slot.H / 2,
    });

    this._returnSlots.push(slot);

    // Run return animation loop
    const returnSpring = this._returnSpring;
    const springCfg = this._springCfg;
    const self = this;

    const runReturn = () => {
      // Spring all params toward 0
      let totalEnergy = 0;
      for (const k of Object.keys(springCfg)) {
        const acc = (slot.paramTarget[k] - slot.paramCur[k]) * returnSpring.stiff;
        slot.paramVel[k] = (slot.paramVel[k] + acc) * returnSpring.damp;
        slot.paramCur[k] += slot.paramVel[k];
        totalEnergy += Math.abs(slot.paramVel[k]) + Math.abs(slot.paramCur[k]);
      }

      // Build deformed vertices
      self._buildDeformedFor(slot);

      // Apply zoom
      const z = slot.zoom.value;
      let cx = slot.zoom.cx, cy = slot.zoom.cy;
      const hw = slot.W / (2 * z), hh = slot.H / (2 * z);
      cx = Math.max(hw, Math.min(slot.W - hw, cx));
      cy = Math.max(hh, Math.min(slot.H - hh, cy));
      slot.camera.left = cx - hw;
      slot.camera.right = cx + hw;
      slot.camera.top = cy - hh;
      slot.camera.bottom = cy + hh;
      slot.camera.updateProjectionMatrix();

      // Update face positions
      const pos = slot.facePosAttr.array;
      for (let i = 0; i < slot.curLM.length; i++) {
        pos[i * 3] = slot.curLM[i].x;
        pos[i * 3 + 1] = slot.curLM[i].y;
      }
      slot.facePosAttr.needsUpdate = true;

      // Update backing mesh positions
      if (slot.backPosAttr) {
        const bp = slot.backPosAttr.array;
        const fcx = slot.faceCenter.x, fcy = slot.faceCenter.y;
        const scale = 1.006;
        for (let i = 0; i < slot.curLM.length; i++) {
          bp[i * 3]     = fcx + (slot.curLM[i].x - fcx) * scale;
          bp[i * 3 + 1] = fcy + (slot.curLM[i].y - fcy) * scale;
        }
        slot.backPosAttr.needsUpdate = true;
      }

      // Update skin opacity
      if (slot.skinBgMat) {
        let maxd = 0;
        for (let i = 0; i < slot.curLM.length; i++) {
          const dx = slot.curLM[i].x - slot.baseLM[i].x;
          const dy = slot.curLM[i].y - slot.baseLM[i].y;
          const d = dx * dx + dy * dy;
          if (d > maxd) maxd = d;
        }
        maxd = Math.sqrt(maxd);
        slot.skinBgMat.opacity = Math.min(1, maxd / (slot.faceSpherizeR * 0.04));
      }

      slot.renderer.render(slot.scene, slot.camera);

      // Check if done (energy low enough and zoom near 1)
      const zoomDone = Math.abs(slot.zoom.value - 1) < 0.01;
      if (totalEnergy < 0.001 && zoomDone) {
        self._cleanupReturnSlot(slot);
        return;
      }

      slot.rafId = requestAnimationFrame(runReturn);
    };

    slot.rafId = requestAnimationFrame(runReturn);

    // Create new renderer for the next member
    this._createMainRenderer();

    // Clear references so startWarp sets up fresh
    this.scene = null;
    this.camera = null;
    this.facePosAttr = null;
    this.faceColAttr = null;
    this.skinBgMat = null;
    this.isAnimating = false;
    this._isReturning = false;
    this.currentMemberId = null;
    this._faceAnchors = null;
    this.coveredCanvas = null;
  }

  /**
   * Build deformed vertices for a return slot (uses slot's own state).
   */
  _buildDeformedFor(slot) {
    if (!slot.baseLM || !slot.faceAnchors) return;
    const b = slot.baseLM;
    const a = slot.faceAnchors;
    const params = slot.paramCur;
    const { bulge, mouth, eye, nose, spherize,
            eyeRotL, eyeRotR, noseRot,
            eyeShiftX, eyeShiftY, noseShiftX, noseShiftY,
            mouthShiftX = 0, mouthShiftY = 0 } = params;

    for (let i = 0; i < b.length; i++) {
      const p = b[i];
      let dx = 0, dy = 0;

      if (spherize > 0) {
        const ex = p.x - slot.faceCenter.x, ey = p.y - slot.faceCenter.y;
        const r = Math.hypot(ex, ey);
        const sRb = slot.faceSpherizeR * 1.15;
        if (r < sRb && r > 0.01) {
          const t = r / sRb;
          const newR = sRb * (1 - Math.pow(1 - t, 1 + spherize * 1.8));
          const k = newR / r - 1;
          dx += ex * k; dy += ey * k;
        }
      }

      if (bulge !== 0) {
        const ex = p.x - a.fc.x, ey = p.y - a.fc.y, r = Math.hypot(ex, ey);
        if (r < a.fR * 1.4 && r > 0.01) {
          const w = smooth(1 - r / (a.fR * 1.4));
          const amt = bulge * w * a.fR * 0.5;
          dx += ex / r * amt; dy += ey / r * amt;
        }
      }

      {
        const ex = p.x - a.mCx, ey = p.y - a.mCy, r = Math.hypot(ex, ey);
        const reach = a.mR * 2.6;
        if (r < reach) {
          const w = smooth(1 - r / reach);
          if (mouth > 0) {
            dx += ex * mouth * w * 0.8;
            dy += ey * mouth * w * 0.4;
            dy += Math.sign(ey || 1) * mouth * w * a.mR * 0.9;
          }
          dx += mouthShiftX * w * a.mR;
          dy += mouthShiftY * w * a.mR;
        }
      }

      const eyeCenters = [
        { c: a.leC, rot: eyeRotL },
        { c: a.reC, rot: eyeRotR },
      ];
      for (const { c: ec, rot } of eyeCenters) {
        const ex = p.x - ec.x, ey = p.y - ec.y, r = Math.hypot(ex, ey);
        const reach = a.eR * 2.2;
        if (r < reach && r > 0.01) {
          const w = smooth(1 - r / reach);
          if (eye !== 0) {
            const amt = eye * w * a.eR * 0.9;
            dx += ex / r * amt; dy += ey / r * amt;
          }
          if (rot !== 0) {
            const cosR = Math.cos(rot * w), sinR = Math.sin(rot * w);
            dx += (cosR * ex - sinR * ey) - ex;
            dy += (sinR * ex + cosR * ey) - ey;
          }
          dx += eyeShiftX * w * a.eR;
          dy += eyeShiftY * w * a.eR;
        }
      }

      {
        const ex = p.x - a.nt.x, ey = p.y - a.nt.y, r = Math.hypot(ex, ey);
        const reach = a.nR * 2.2;
        if (r < reach && r > 0.01) {
          const w = smooth(1 - r / reach);
          if (nose !== 0) {
            const amt = nose * w * a.nR * 0.8;
            dx += (ex / r) * amt; dy += (ey / r) * amt;
          }
          if (noseRot !== 0) {
            const cosR = Math.cos(noseRot * w), sinR = Math.sin(noseRot * w);
            dx += (cosR * ex - sinR * ey) - ex;
            dy += (sinR * ex + cosR * ey) - ey;
          }
          dx += noseShiftX * w * a.nR;
          dy += noseShiftY * w * a.nR;
        }
      }

      slot.curLM[i] = { x: p.x + dx, y: p.y + dy };
    }
  }

  _cleanupReturnSlot(slot) {
    if (slot.zoomTween) { slot.zoomTween.kill(); slot.zoomTween = null; }
    if (slot.rafId) { cancelAnimationFrame(slot.rafId); slot.rafId = null; }
    if (slot.canvas.parentNode) {
      slot.canvas.parentNode.removeChild(slot.canvas);
    }
    if (slot.scene) {
      slot.scene.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (child.material.map) child.material.map.dispose();
          child.material.dispose();
        }
      });
    }
    slot.renderer.dispose();
    const idx = this._returnSlots.indexOf(slot);
    if (idx >= 0) this._returnSlots.splice(idx, 1);
  }

  async loadLandmarks() {
    try {
      const res = await fetch('/assets/data/landmarks.json');
      this.landmarksData = await res.json();
    } catch (e) {
      // landmarks.json not found — warp disabled
      this.landmarksData = {};
    }
  }

  hasLandmarks(memberId) {
    return this.landmarksData && this.landmarksData[memberId];
  }

  onMouseMove(x, y) {
    if (!this.isAnimating || !this.camera) return;
    // Convert CSS coords to mesh space (accounting for camera zoom)
    const cam = this.camera;
    const meshX = cam.left + (x / this.W) * (cam.right - cam.left);
    const meshY = cam.top + (y / this.H) * (cam.bottom - cam.top);
    this._mouse.targetX = meshX;
    this._mouse.targetY = meshY;
    this._mouse.active = true;
  }

  startWarp(memberId, photoEl, imgEl) {
    if (!this.hasLandmarks(memberId)) return;
    if (this.isAnimating && this.currentMemberId === memberId && !this._isReturning) return;

    // If currently animating another member, spawn return animation on old canvas
    if (this.isAnimating && this.scene) {
      this._spawnReturnAnim();
    } else {
      this.stopWarp(true);
    }

    this.currentMemberId = memberId;
    const rect = photoEl.getBoundingClientRect();
    this.W = Math.round(rect.width);
    this.H = Math.round(rect.height);

    this.renderer.setSize(this.W, this.H, false);
    photoEl.appendChild(this.canvas);
    if (this._overlayCanvas) {
      this._overlayCanvas.width = this.W;
      this._overlayCanvas.height = this.H;
      photoEl.appendChild(this._overlayCanvas);
    }

    const rawLM = this.landmarksData[memberId];
    const natW = rawLM.imageWidth;
    const natH = rawLM.imageHeight;

    this.coveredCanvas = this._drawCovered(imgEl, natW, natH, this.W, this.H);
    this.baseLM = this._transformLandmarks(rawLM.landmarks, natW, natH, this.W, this.H);

    let ox = 0, oy = 0;
    for (const i of FACE_OVAL) { ox += this.baseLM[i].x; oy += this.baseLM[i].y; }
    ox /= FACE_OVAL.length; oy /= FACE_OVAL.length;
    this.faceCenter = { x: ox, y: oy };
    let rsum = 0;
    for (const i of FACE_OVAL) { rsum += Math.hypot(this.baseLM[i].x - ox, this.baseLM[i].y - oy); }
    this.faceSpherizeR = rsum / FACE_OVAL.length;

    // Compute face feature anchors (same as reference)
    this._faceAnchors = this._computeFaceAnchors();

    this.curLM = this.baseLM.map(p => ({ ...p }));

    // Per-param spring state: current, target, velocity (all start at 0)
    const paramNames = Object.keys(this._springCfg);
    this._paramCur = {};
    this._paramTarget = {};
    this._paramVel = {};
    for (const k of paramNames) {
      this._paramCur[k] = 0;
      this._paramTarget[k] = 0;
      this._paramVel[k] = 0;
    }
    this._targetBuilt = false;
    this._baseTarget = null;
    this._frameCount = 0;

    // Reset mouse state
    this._mouse.curX = this.faceCenter.x;
    this._mouse.curY = this.faceCenter.y;
    this._mouse.targetX = this.faceCenter.x;
    this._mouse.targetY = this.faceCenter.y;
    this._mouse.velX = 0;
    this._mouse.velY = 0;
    this._mouse.active = false;

    this._setupScene();

    // Zoom animation
    if (this.zoomTween) { this.zoomTween.kill(); this.zoomTween = null; }
    this.zoom = { value: 1, cx: this.W / 2, cy: this.H / 2 };
    this.isAnimating = true;

    this.zoomTween = gsap.to(this.zoom, {
      duration: 0.7,
      ease: 'power4.out',
      value: this._memberZoom[memberId] || this.zoomAmount,
      cx: this.faceCenter.x,
      cy: this.faceCenter.y,
    });

    // Start render loop
    this._renderLoop();
  }

  _initGUI() {
    this._gui = new GUI({ title: 'Face Warp' });
    this._gui.domElement.style.display = 'none';

    window.addEventListener('keydown', (e) => {
      if (e.key === 'd' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const el = this._gui.domElement;
        el.style.display = el.style.display === 'none' ? '' : 'none';
      }
    });

    // Range folder: max random range per param
    const rangeFolder = this._gui.addFolder('Range (max)');
    rangeFolder.add(this._paramRange, 'bulge', 0, 1.5, 0.01).name('Bulge');
    rangeFolder.add(this._paramRange, 'mouth', 0, 0.8, 0.01).name('Mouth');
    rangeFolder.add(this._paramRange, 'eye', -0.3, 1.4, 0.01).name('Eye');
    rangeFolder.add(this._paramRange, 'nose', 0, 0.8, 0.01).name('Nose');
    rangeFolder.add(this._paramRange, 'spherize', 0, 4.0, 0.01).name('Spherize');
    rangeFolder.add(this._paramRange, 'eyeRotL', 0, 0.8, 0.01).name('Eye Rot L');
    rangeFolder.add(this._paramRange, 'eyeRotR', 0, 0.8, 0.01).name('Eye Rot R');
    rangeFolder.add(this._paramRange, 'noseRot', 0, 0.6, 0.01).name('Nose Rot');
    rangeFolder.add(this._paramRange, 'eyeShiftX', 0, 1.0, 0.01).name('Eye Shift X');
    rangeFolder.add(this._paramRange, 'eyeShiftY', 0, 1.0, 0.01).name('Eye Shift Y');
    rangeFolder.add(this._paramRange, 'noseShiftX', 0, 1.0, 0.01).name('Nose Shift X');
    rangeFolder.add(this._paramRange, 'noseShiftY', 0, 1.0, 0.01).name('Nose Shift Y');

    // Preview folder: directly control param values
    const previewFolder = this._gui.addFolder('Preview (direct)');
    previewFolder.add(this._preview, 'enabled').name('Enable Preview');
    previewFolder.add(this._preview, 'bulge', -1.5, 1.5, 0.01).name('Bulge');
    previewFolder.add(this._preview, 'mouth', 0, 2.0, 0.01).name('Mouth');
    previewFolder.add(this._preview, 'eye', -0.3, 1.4, 0.01).name('Eye');
    previewFolder.add(this._preview, 'nose', -1.5, 1.5, 0.01).name('Nose');
    previewFolder.add(this._preview, 'spherize', 0, 4.0, 0.01).name('Spherize');
    previewFolder.add(this._preview, 'eyeRotL', -0.8, 0.8, 0.01).name('Eye Rot L');
    previewFolder.add(this._preview, 'eyeRotR', -0.8, 0.8, 0.01).name('Eye Rot R');
    previewFolder.add(this._preview, 'noseRot', -0.6, 0.6, 0.01).name('Nose Rot');
    previewFolder.add(this._preview, 'eyeShiftX', -1.0, 1.0, 0.01).name('Eye Shift X');
    previewFolder.add(this._preview, 'eyeShiftY', -1.0, 1.0, 0.01).name('Eye Shift Y');
    previewFolder.add(this._preview, 'noseShiftX', -1.0, 1.0, 0.01).name('Nose Shift X');
    previewFolder.add(this._preview, 'noseShiftY', -1.0, 1.0, 0.01).name('Nose Shift Y');
    previewFolder.close();

    // Mouse interaction folder
    const mouseFolder = this._gui.addFolder('Mouse Interaction');
    mouseFolder.add(this._mouse, 'influence', 0, 2.0, 0.05).name('Influence');
    mouseFolder.add(this._mouse, 'stiff', 0.01, 0.2, 0.005).name('Stiffness');
    mouseFolder.add(this._mouse, 'damp', 0.7, 0.95, 0.01).name('Damping');

    // Debug view toggle (separate fixed UI)
    this._debugView = { mesh: false };
    this._initDebugButtons();

  }

  _initDebugButtons() {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'position:fixed;bottom:16px;right:16px;z-index:9999;display:flex;gap:6px;';

    const makeBtn = (label, key) => {
      const btn = document.createElement('button');
      btn.textContent = label;
      btn.style.cssText = 'font-size:11px;padding:5px 12px;border:1px solid #333;border-radius:6px;background:transparent;color:#555;cursor:pointer;font-family:inherit;';
      btn.addEventListener('click', () => {
        this._debugView[key] = !this._debugView[key];
        btn.style.borderColor = this._debugView[key] ? '#666' : '#333';
        btn.style.color = this._debugView[key] ? '#ccc' : '#555';
        btn.style.background = this._debugView[key] ? '#222' : 'transparent';
      });
      return btn;
    };

    wrap.appendChild(makeBtn('mesh', 'mesh'));
    document.body.appendChild(wrap);
  }

  _cleanup() {
    if (this.zoomTween) { this.zoomTween.kill(); this.zoomTween = null; }
    this.isAnimating = false;
    this._isReturning = false;
    this.currentMemberId = null;
    this._faceAnchors = null;
    this._targetBuilt = false;
    this._paramCur = null;
    this._paramTarget = null;
    this._paramVel = null;
    if (this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
    if (this._overlayCanvas && this._overlayCanvas.parentNode) {
      this._overlayCanvas.parentNode.removeChild(this._overlayCanvas);
    }
    if (this.scene) {
      this.scene.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (child.material.map) child.material.map.dispose();
          child.material.dispose();
        }
      });
      this.scene = null;
    }
    this.coveredCanvas = null;
  }

  /**
   * Compute face feature anchor points (same logic as reference buildDeformed).
   */
  _computeFaceAnchors() {
    const b = this.baseLM;
    const fc = avgPts([10, 152, 234, 454], b);
    const fR = dist(b[10], b[152]) * 0.55;
    const leC = avgPts([33, 133], b);
    const reC = avgPts([362, 263], b);
    const eR = dist(b[33], b[133]) * 0.9;
    const nt = b[1];
    const nR = dist(b[98], b[327]) * 1.1;

    let mcx = 0, mcy = 0;
    for (const i of MOUTH_OUTER) { mcx += b[i].x; mcy += b[i].y; }
    mcx /= MOUTH_OUTER.length; mcy /= MOUTH_OUTER.length;
    let mrsum = 0;
    for (const i of MOUTH_OUTER) { mrsum += Math.hypot(b[i].x - mcx, b[i].y - mcy); }
    const mR = mrsum / MOUTH_OUTER.length;

    return { fc, fR, leC, reC, eR, nt, nR, mCx: mcx, mCy: mcy, mR };
  }

  /**
   * Build deformed landmarks using current instance state.
   */
  _buildDeformed(params) {
    this._buildDeformedFor({
      baseLM: this.baseLM,
      faceAnchors: this._faceAnchors,
      faceCenter: this.faceCenter,
      faceSpherizeR: this.faceSpherizeR,
      curLM: this.curLM,
      paramCur: params,
    });
  }

  _renderLoop() {
    if (!this.isAnimating) return;

    this._frameCount++;

    // Preview mode: GUI directly controls param targets
    if (this._preview.enabled && !this._isReturning) {
      for (const k of Object.keys(this._springCfg)) {
        this._paramTarget[k] = this._preview[k];
      }
      this._targetBuilt = true;
    }

    // Set random base targets once on hover start
    if (!this._targetBuilt && !this._isReturning) {
      this._targetBuilt = true;
      const r = this._paramRange;
      const rng = (range) => (Math.random() * 2 - 1) * range;
      const rngPos = (range) => Math.random() * range;
      this._baseTarget = {
        bulge:      rng(r.bulge),
        mouth:      rngPos(r.mouth),
        eye:        -0.3 + Math.random() * 0.8,
        nose:       -0.2 + Math.random() * 1.0,
        spherize:   0.15 + rngPos(r.spherize),
        eyeRotL:    rng(r.eyeRotL),
        eyeRotR:    rng(r.eyeRotR),
        noseRot:    rng(r.noseRot),
        eyeShiftX:  rng(r.eyeShiftX),
        eyeShiftY:  rng(r.eyeShiftY),
        noseShiftX: rng(r.noseShiftX),
        noseShiftY: rng(r.noseShiftY),
        mouthShiftX: rng(r.mouthShiftX),
        mouthShiftY: rng(r.mouthShiftY),
      };
      // Copy base targets as initial param targets
      for (const k of Object.keys(this._baseTarget)) {
        this._paramTarget[k] = this._baseTarget[k];
      }
    }

    // Mouse-driven parameter modulation: proximity selects feature, velocity scales amount
    if (this._mouse.active && !this._isReturning && this._faceAnchors && this._baseTarget) {
      const m = this._mouse;
      const a = this._faceAnchors;
      const mx = m.curX, my = m.curY;
      const inf = m.influence;
      const r = this._paramRange;

      // Mouse velocity magnitude (clamped to 0-1)
      const vel = Math.min(1, Math.hypot(m.velX, m.velY) / (this.faceSpherizeR * 0.08));

      // Proximity to each feature (0 = far, 1 = on top)
      // Minimum radius based on image size so small faces still react
      const minR = Math.min(this.W, this.H) * 0.25;
      const prox = (cx, cy, radius) => {
        const d = Math.hypot(mx - cx, my - cy);
        return Math.max(0, 1 - d / Math.max(radius, minR));
      };
      const pLeye   = prox(a.leC.x, a.leC.y, a.eR * 4);
      const pReye   = prox(a.reC.x, a.reC.y, a.eR * 4);
      const pNose   = prox(a.nt.x,  a.nt.y,  a.nR * 3);
      const pMouth  = prox(a.mCx,   a.mCy,   a.mR * 3);
      const pCenter = prox(a.fc.x,  a.fc.y,  Math.max(this.faceSpherizeR * 0.8, minR));

      // Mouse direction relative to face center
      const dirX = (mx - a.fc.x) / (this.faceSpherizeR || 1);
      const dirY = (my - a.fc.y) / (this.faceSpherizeR || 1);

      const bt = this._baseTarget;
      const v = vel; // velocity scale: no movement = no mouse effect
      this._paramTarget.eyeRotL     = bt.eyeRotL     + pLeye  * v * inf * r.eyeRotL   * dirX * 3.0;
      this._paramTarget.eyeRotR     = bt.eyeRotR     + pReye  * v * inf * r.eyeRotR   * dirX * 3.0;
      this._paramTarget.eyeShiftX   = bt.eyeShiftX   + Math.max(pLeye, pReye) * v * inf * r.eyeShiftX * dirX * 2.8;
      this._paramTarget.eyeShiftY   = bt.eyeShiftY   + Math.max(pLeye, pReye) * v * inf * r.eyeShiftY * dirY * 2.8;
      this._paramTarget.eye         = bt.eye;
      this._paramTarget.noseRot     = bt.noseRot     + pNose  * v * inf * r.noseRot    * dirX * 3.0;
      this._paramTarget.noseShiftX  = bt.noseShiftX  + pNose  * v * inf * r.noseShiftX * dirX * 2.8;
      this._paramTarget.noseShiftY  = bt.noseShiftY  + pNose  * v * inf * r.noseShiftY * dirY * 2.8;
      this._paramTarget.nose        = bt.nose;
      this._paramTarget.mouth       = bt.mouth;
      this._paramTarget.mouthShiftX = bt.mouthShiftX + pMouth * v * inf * r.mouthShiftX * dirX * 2.5;
      this._paramTarget.mouthShiftY = bt.mouthShiftY + pMouth * v * inf * r.mouthShiftY * dirY * 2.5;
      this._paramTarget.spherize    = bt.spherize    + pCenter * v * inf * r.spherize * 0.8;
      this._paramTarget.bulge       = bt.bulge       + pCenter * v * inf * r.bulge * 0.6;
    }

    // During return, all param targets = 0
    if (this._isReturning) {
      for (const k of Object.keys(this._paramTarget)) {
        this._paramTarget[k] = 0;
      }
    }

    // Spring each param individually toward its target (with staggered delay)
    for (const k of Object.keys(this._springCfg)) {
      const cfg = this._springCfg[k];
      // Delay: param doesn't start moving until frameCount exceeds its delay
      if (!this._isReturning && this._frameCount < cfg.delay) continue;

      const spring = this._isReturning ? this._returnSpring : cfg;
      const acc = (this._paramTarget[k] - this._paramCur[k]) * spring.stiff;
      this._paramVel[k] = (this._paramVel[k] + acc) * spring.damp;
      this._paramCur[k] += this._paramVel[k];
    }

    // Spring the mouse influence position toward actual mouse
    if (this._mouse.active) {
      const m = this._mouse;
      const accX = (m.targetX - m.curX) * m.stiff;
      const accY = (m.targetY - m.curY) * m.stiff;
      m.velX = (m.velX + accX) * m.damp;
      m.velY = (m.velY + accY) * m.damp;
      m.curX += m.velX;
      m.curY += m.velY;
    }

    // Build vertex positions from sprung param values + mouse push
    this._buildDeformed(this._paramCur);

    this._applyZoom();
    this._renderFrame();

    this._rafId = requestAnimationFrame(() => this._renderLoop());
  }

  stopWarp(immediate) {
    if (this.zoomTween) { this.zoomTween.kill(); this.zoomTween = null; }
    if (!this.isAnimating && !this.currentMemberId) return;

    if (immediate) {
      this._stopRenderLoop();
      this._cleanup();
      return;
    }

    this._isReturning = true;

    this.zoomTween = gsap.to(this.zoom, {
      duration: 0.6,
      ease: 'power4.out',
      value: 1,
      cx: this.W / 2,
      cy: this.H / 2,
      onComplete: () => {
        this._stopRenderLoop();
        this._cleanup();
      },
    });
  }

  _stopRenderLoop() {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  _drawCovered(imgEl, natW, natH, dispW, dispH) {
    const dpr = Math.min(window.devicePixelRatio, 2);
    const canvas = document.createElement('canvas');
    canvas.width = dispW * dpr;
    canvas.height = dispH * dpr;
    const ctx = canvas.getContext('2d');

    const natAspect = natW / natH;
    const dispAspect = dispW / dispH;

    let sw, sh, sx, sy;
    if (natAspect > dispAspect) {
      sh = natH;
      sw = natH * dispAspect;
      sx = (natW - sw) / 2;
      sy = 0;
    } else {
      sw = natW;
      sh = natW / dispAspect;
      sx = 0;
      sy = (natH - sh) / 2;
    }

    ctx.drawImage(imgEl, sx, sy, sw, sh, 0, 0, dispW * dpr, dispH * dpr);
    return canvas;
  }

  _transformLandmarks(landmarks, natW, natH, dispW, dispH) {
    const natAspect = natW / natH;
    const dispAspect = dispW / dispH;

    let scale, cropX, cropY;
    if (natAspect > dispAspect) {
      scale = dispH / natH;
      const visibleW = natH * dispAspect;
      cropX = (natW - visibleW) / 2;
      cropY = 0;
    } else {
      scale = dispW / natW;
      const visibleH = natW / dispAspect;
      cropX = 0;
      cropY = (natH - visibleH) / 2;
    }

    return landmarks.map(p => ({
      x: (p.x * natW - cropX) * scale,
      y: (p.y * natH - cropY) * scale,
    }));
  }

  _setupScene() {
    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(0, this.W, 0, this.H, -1000, 1000);
    this.camera.position.z = 10;

    const tex = new THREE.Texture(this.coveredCanvas);
    tex.flipY = false;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.needsUpdate = true;

    const quadPos = new Float32Array([0, 0, 0, this.W, 0, 0, this.W, this.H, 0, 0, this.H, 0]);
    const quadUv = new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]);
    const quadIdx = [0, 1, 2, 0, 2, 3];

    const bgGeo = new THREE.BufferGeometry();
    bgGeo.setAttribute('position', new THREE.BufferAttribute(quadPos.slice(), 3));
    bgGeo.setAttribute('uv', new THREE.BufferAttribute(quadUv.slice(), 2));
    bgGeo.setIndex(quadIdx.slice());
    const bgMesh = new THREE.Mesh(bgGeo, new THREE.MeshBasicMaterial({
      map: tex, side: THREE.DoubleSide, depthTest: false, depthWrite: false,
    }));
    bgMesh.renderOrder = 0;
    this.scene.add(bgMesh);

    // Skin blur background
    const dpr = Math.min(window.devicePixelRatio, 2);
    const skinCanvas = document.createElement('canvas');
    skinCanvas.width = this.W * dpr;
    skinCanvas.height = this.H * dpr;
    const skx = skinCanvas.getContext('2d');
    skx.filter = 'blur(24px)';
    skx.drawImage(this.coveredCanvas, 0, 0, this.W * dpr, this.H * dpr);
    skx.filter = 'none';

    const ocx = this.faceCenter.x, ocy = this.faceCenter.y;
    let maxr = 0;
    for (const i of FACE_OVAL) {
      maxr = Math.max(maxr, Math.hypot(this.baseLM[i].x - ocx, this.baseLM[i].y - ocy));
    }
    skx.globalCompositeOperation = 'destination-in';
    const grad = skx.createRadialGradient(
      ocx * dpr, ocy * dpr, maxr * 0.3 * dpr,
      ocx * dpr, ocy * dpr, maxr * 0.98 * dpr
    );
    grad.addColorStop(0, 'rgba(0,0,0,1)');
    grad.addColorStop(0.75, 'rgba(0,0,0,1)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    skx.fillStyle = grad;
    skx.beginPath();
    for (let k = 0; k < FACE_OVAL.length; k++) {
      const p = this.baseLM[FACE_OVAL[k]];
      if (k === 0) skx.moveTo(p.x * dpr, p.y * dpr); else skx.lineTo(p.x * dpr, p.y * dpr);
    }
    skx.closePath();
    skx.fill();
    skx.globalCompositeOperation = 'source-over';

    const mergedCanvas = document.createElement('canvas');
    mergedCanvas.width = this.W * dpr;
    mergedCanvas.height = this.H * dpr;
    const mCtx = mergedCanvas.getContext('2d');
    mCtx.drawImage(this.coveredCanvas, 0, 0);
    mCtx.drawImage(skinCanvas, 0, 0);

    const skinTex = new THREE.Texture(mergedCanvas);
    skinTex.flipY = false;
    skinTex.minFilter = THREE.LinearFilter;
    skinTex.magFilter = THREE.LinearFilter;
    skinTex.needsUpdate = true;

    const skinGeo = new THREE.BufferGeometry();
    skinGeo.setAttribute('position', new THREE.BufferAttribute(quadPos.slice(), 3));
    skinGeo.setAttribute('uv', new THREE.BufferAttribute(quadUv.slice(), 2));
    skinGeo.setIndex(quadIdx.slice());
    this.skinBgMat = new THREE.MeshBasicMaterial({
      map: skinTex, side: THREE.DoubleSide, transparent: true, opacity: 0, depthTest: false, depthWrite: false,
    });
    const skinMesh = new THREE.Mesh(skinGeo, this.skinBgMat);
    skinMesh.renderOrder = 1;
    this.scene.add(skinMesh);

    // Face mesh
    const n = this.baseLM.length;
    const pos = new Float32Array(n * 3);
    const uv = new Float32Array(n * 2);
    const col = new Float32Array(n * 3).fill(1);

    for (let i = 0; i < n; i++) {
      pos[i * 3] = this.baseLM[i].x;
      pos[i * 3 + 1] = this.baseLM[i].y;
      pos[i * 3 + 2] = 1;
      uv[i * 2] = this.baseLM[i].x / this.W;
      uv[i * 2 + 1] = this.baseLM[i].y / this.H;
    }

    this.facePosAttr = new THREE.BufferAttribute(pos, 3);
    this.facePosAttr.setUsage(THREE.DynamicDrawUsage);

    this.faceColAttr = new THREE.BufferAttribute(col, 3);
    this.faceColAttr.setUsage(THREE.DynamicDrawUsage);

    const faceGeo = new THREE.BufferGeometry();
    faceGeo.setAttribute('position', this.facePosAttr);
    faceGeo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    faceGeo.setAttribute('color', this.faceColAttr);
    faceGeo.setIndex(TRIS);

    // Backing mesh: slightly larger copy behind the main mesh to fill edge gaps
    const backPos = new Float32Array(pos);
    this._backPosAttr = new THREE.BufferAttribute(backPos, 3);
    this._backPosAttr.setUsage(THREE.DynamicDrawUsage);
    const backGeo = new THREE.BufferGeometry();
    backGeo.setAttribute('position', this._backPosAttr);
    backGeo.setAttribute('uv', new THREE.BufferAttribute(uv.slice(), 2));
    backGeo.setIndex(TRIS.slice());
    const backMat = new THREE.MeshBasicMaterial({
      map: tex, side: THREE.DoubleSide,
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.NoBlending,
    });
    const backMesh = new THREE.Mesh(backGeo, backMat);
    backMesh.renderOrder = 1.5;
    this.scene.add(backMesh);

    const faceMat = new THREE.MeshBasicMaterial({
      map: tex, vertexColors: true, side: THREE.DoubleSide,
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.NoBlending,
    });
    const faceMesh = new THREE.Mesh(faceGeo, faceMat);
    faceMesh.renderOrder = 2;
    this.scene.add(faceMesh);
  }

  _applyZoom() {
    if (!this.camera) return;
    const z = this.zoom.value;
    let cx = this.zoom.cx;
    let cy = this.zoom.cy;
    const hw = this.W / (2 * z);
    const hh = this.H / (2 * z);

    cx = Math.max(hw, Math.min(this.W - hw, cx));
    cy = Math.max(hh, Math.min(this.H - hh, cy));

    this.camera.left = cx - hw;
    this.camera.right = cx + hw;
    this.camera.top = cy - hh;
    this.camera.bottom = cy + hh;
    this.camera.updateProjectionMatrix();
  }

  _renderFrame() {
    if (!this.renderer || !this.curLM || !this.scene) return;

    const pos = this.facePosAttr.array;
    for (let i = 0; i < this.curLM.length; i++) {
      pos[i * 3] = this.curLM[i].x;
      pos[i * 3 + 1] = this.curLM[i].y;
    }
    this.facePosAttr.needsUpdate = true;

    // Backing mesh: same shape but slightly expanded from face center to fill edge gaps
    if (this._backPosAttr) {
      const bp = this._backPosAttr.array;
      const fcx = this.faceCenter.x, fcy = this.faceCenter.y;
      const scale = 1.006;
      for (let i = 0; i < this.curLM.length; i++) {
        bp[i * 3]     = fcx + (this.curLM[i].x - fcx) * scale;
        bp[i * 3 + 1] = fcy + (this.curLM[i].y - fcy) * scale;
      }
      this._backPosAttr.needsUpdate = true;
    }

    if (this.skinBgMat) {
      let maxd = 0;
      for (let i = 0; i < this.curLM.length; i++) {
        const dx = this.curLM[i].x - this.baseLM[i].x;
        const dy = this.curLM[i].y - this.baseLM[i].y;
        const d = dx * dx + dy * dy;
        if (d > maxd) maxd = d;
      }
      maxd = Math.sqrt(maxd);
      this.skinBgMat.opacity = Math.min(1, maxd / (this.faceSpherizeR * 0.04));
    }

    if (this.faceColAttr) {
      const curGap = Math.hypot(this.curLM[13].x - this.curLM[14].x, this.curLM[13].y - this.curLM[14].y);
      const restGap = Math.hypot(this.baseLM[13].x - this.baseLM[14].x, this.baseLM[13].y - this.baseLM[14].y);
      const ref = Math.hypot(this.baseLM[10].x - this.baseLM[152].x, this.baseLM[10].y - this.baseLM[152].y);
      const open = Math.min(1, Math.max(0, (curGap - restGap) / (ref * 0.05)));
      const col = this.faceColAttr.array;
      for (const idx of MOUTH_INNER) {
        col[idx * 3] = 1 + (0.06 - 1) * open;
        col[idx * 3 + 1] = 1 + (0.02 - 1) * open;
        col[idx * 3 + 2] = 1 + (0.03 - 1) * open;
      }
      this.faceColAttr.needsUpdate = true;
    }

    this.renderer.render(this.scene, this.camera);

    // Debug overlay drawing
    if (this._overlayCtx && this.curLM && this._debugView.mesh) {
      const oCtx = this._overlayCtx;
      const ow = this._overlayCanvas.width;
      const oh = this._overlayCanvas.height;
      oCtx.clearRect(0, 0, ow, oh);

      const cam = this.camera;
      const toScreen = (p) => ({
        x: ((p.x - cam.left) / (cam.right - cam.left)) * ow,
        y: ((p.y - cam.top) / (cam.bottom - cam.top)) * oh,
      });

      oCtx.strokeStyle = 'rgba(0,255,160,0.4)';
      oCtx.lineWidth = 0.6;
      for (let i = 0; i < TRIS.length; i += 3) {
        const a = toScreen(this.curLM[TRIS[i]]);
        const b = toScreen(this.curLM[TRIS[i + 1]]);
        const c = toScreen(this.curLM[TRIS[i + 2]]);
        oCtx.beginPath();
        oCtx.moveTo(a.x, a.y);
        oCtx.lineTo(b.x, b.y);
        oCtx.lineTo(c.x, c.y);
        oCtx.closePath();
        oCtx.stroke();
      }

    } else if (this._overlayCtx) {
      this._overlayCtx.clearRect(0, 0, this._overlayCanvas.width, this._overlayCanvas.height);
    }
  }
}
